import { useMemo, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import youbora from 'youboralib';

let plugin;
function NpawMonitoring({
  adapter = void 0,
  adsAdapter = void 0,
  accountCode,
  isLive = void 0,
  isBlim = false,
  content = void 0,
  userId = void 0,
  profileId = void 0,
  channelName = void 0,
  channelId = void 0,
  userType = void 0,
  playerInfo = void 0,
  playSource = void 0,
  chromecastSender = void 0,
  deviceMetadata = void 0,
  vodPlayStartTime = 0,
  msoProvider = void 0,
  error = void 0,
  providerId = void 0,
  subscriberId = void 0
}) {
  const isEpisode = useMemo(() => content?.type?.includes("EPISODE"), [content?.type]);
  const contentTitle = useMemo(() => {
    if (isLive)
      return `LIVE-${channelName || ""}`;
    if (isEpisode) {
      return `VOD-${content?.episode?.showName || ""}-S${content?.episode?.seasonNumber || ""}E${content?.episode?.number || ""}-${content?.title || ""}`;
    }
    return `VOD-${content?.title || ""}`;
  }, [
    channelName,
    content,
    isEpisode,
    isLive
  ]);
  const contentType = useMemo(() => content?.type?.replace(/(LIVE|VOD)_/, ""), [content?.type]);
  const appVersion = useMemo(() => playerInfo?.APP_VERSION?.replace(/-local/i, ""), [playerInfo?.APP_VERSION]);
  const getPlugin = useCallback(() => {
    if (!plugin) {
      plugin = new youbora.Plugin({ accountCode });
    }
    return plugin;
  }, [accountCode]);
  useEffect(() => {
    if (adapter) {
      getPlugin().setAdapter(adapter);
    }
  }, [adapter, getPlugin]);
  useEffect(() => {
    if (adsAdapter) {
      getPlugin().setAdsAdapter(adsAdapter);
    }
  }, [adsAdapter, getPlugin]);
  useEffect(() => {
    getPlugin().setOptions({
      // APP
      "app.name": playerInfo.PLAYER_NAME,
      "app.releaseVersion": appVersion
    });
  }, [getPlugin, playerInfo, appVersion]);
  useEffect(() => {
    getPlugin().setOptions({
      "device.model": deviceMetadata?.model,
      "device.osVersion": deviceMetadata?.osVersion,
      "content.customDimension.8": deviceMetadata?.firmwareVersion
      // firmwareVersion - Only for HTML5. (kind of OS version, not the browser version). Example: "04.80.10"
    });
  }, [deviceMetadata, getPlugin]);
  useEffect(() => {
    getPlugin().setOptions({
      "content.customDimension.7": chromecastSender
      // chromecastSender - Only for Chromecast. Example: "DTVGo iOS - 4.0.0"
    });
  }, [chromecastSender, getPlugin]);
  useEffect(() => {
    getPlugin().setOptions({
      // USER
      "user.type": userId ? "Registered" : "Unregistered",
      "user.name": userId,
      "content.customDimension.3": userType?.toUpperCase(),
      // userLogin - Origem do assinante. Exemplos: TVE, OTT, SKY-DTH,ALGAR-OTT
      "content.customDimension.13": profileId || "",
      "content.customDimension.14": getValueOrNotAvailable(subscriberId)
    });
  }, [getPlugin, userId, profileId, userType, subscriberId]);
  useEffect(() => {
    getPlugin().setOptions({
      // CONTENT
      "content.title": contentTitle,
      "content.program": content?.episode?.showName || content?.title,
      "content.channel": getValueOrEmpty(channelName),
      "content.id": getValueOrEmpty(content?.vrioAssetId),
      "content.season": getValueOrEmpty(content?.episode?.seasonNumber),
      "content.episodeTitle": getValueOrNotApplicable(isEpisode && content?.title),
      "content.genre": getValueOrEmpty(content?.genres?.get?.(0) || content?.genres?.[0]),
      "content.type": contentType,
      "content.tvShow": getValueOrNotApplicable(content?.episode?.showName),
      "content.saga": getValueOrEmpty(content?.episode?.showName),
      "content.gracenoteId": getValueOrEmpty(content?.metadata?.id),
      "content.isLive": isLive,
      "content.playbackType": isLive ? "LIVE" : "VOD",
      // CUSTOM
      "content.customDimension.1": getValueOrEmpty(playSource),
      // playSource - Where the play started, examples: "LiveLander-EPG"/ "HomeLander-FeaturedCarousel"
      "content.customDimension.2": getValueOrNotAvailable(providerId),
      // providerId
      "content.customDimension.4": getValueOrEmpty(channelId),
      // channelId
      "content.customDimension.5": getValueOrNotApplicable(content?.episode?.number),
      // episodeNumber
      "content.customDimension.6": getValueOrEmpty(content?.genres?.join(", ")),
      // genreList - This is the full list separated by comma.
      "content.customDimension.9": getValueOrEmpty(content?.rating),
      // rating - Example: "L", "18"
      "content.customDimension.10": isLive ? "" : getValueOrEmpty(vodPlayStartTime),
      // vodPlayStartTime - In seconds
      "content.customDimension.11": getValueOrEmpty(msoProvider)
    });
  }, [
    isLive,
    content,
    isEpisode,
    channelName,
    channelId,
    playSource,
    vodPlayStartTime,
    adapter,
    adsAdapter,
    contentTitle,
    isBlim,
    getPlugin,
    contentType,
    msoProvider,
    providerId
  ]);
  useEffect(() => {
    const internalAdapter = getPlugin()?.getAdapter();
    if (error) {
      internalAdapter?.fireFatalError(error.name, error.description, error.metadata, "fatal", "NpawMonitoring");
    }
  }, [error, getPlugin]);
  return null;
}
NpawMonitoring.propTypes = {
  accountCode: PropTypes.string.isRequired,
  adapter: PropTypes.shape({}),
  adsAdapter: PropTypes.shape({}),
  playerInfo: PropTypes.shape({
    PLAYER_NAME: PropTypes.string,
    APP_VERSION: PropTypes.string
  }),
  content: PropTypes.shape({
    title: PropTypes.string,
    vrioAssetId: PropTypes.string,
    genres: PropTypes.shape([]),
    type: PropTypes.string,
    rating: PropTypes.string,
    episode: PropTypes.shape({
      showName: PropTypes.string,
      seasonNumber: PropTypes.number,
      number: PropTypes.number
    }),
    metadata: PropTypes.shape({
      id: PropTypes.string
    })
  }),
  userId: PropTypes.string,
  profileId: PropTypes.string,
  userType: PropTypes.string,
  playSource: PropTypes.string,
  deviceMetadata: PropTypes.shape({
    model: PropTypes.string,
    osVersion: PropTypes.string,
    firmwareVersion: PropTypes.string
  }),
  chromecastSender: PropTypes.string,
  vodPlayStartTime: PropTypes.number,
  channelName: PropTypes.string,
  channelId: PropTypes.string,
  isLive: PropTypes.bool,
  isBlim: PropTypes.bool,
  msoProvider: PropTypes.string,
  error: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    metadata: PropTypes.shape()
  }),
  providerId: PropTypes.string,
  subscriberId: PropTypes.string
};
function getValueOrEmpty(value) {
  return value || "";
}
function getValueOrNotApplicable(value) {
  return value || "Not applicable";
}
function getValueOrNotAvailable(value) {
  return value || "Not available";
}

export { NpawMonitoring as default };
//# sourceMappingURL=NpawMonitoring.js.map
